import React, { useState, useEffect, useRef, useCallback } from "react";

// ── Spiral order generator ────────────────────────────────────────────────────
function generateSpiralOrder(rows, cols) {
  const cx = Math.floor(cols / 2);
  const cy = Math.floor(rows / 2);
  const byDist = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const dist = Math.max(Math.abs(r - cy), Math.abs(c - cx));
      if (!byDist[dist]) byDist[dist] = [];
      byDist[dist].push({ r, c });
    }
  }
  const order = [];
  byDist.forEach((ring) => {
    ring.sort(
      (a, b) => Math.atan2(a.r - cy, a.c - cx) - Math.atan2(b.r - cy, b.c - cx),
    );
    ring.forEach((pos) => order.push(pos));
  });
  return order;
}

const ROWS = 6;
const COLS = 9;
const PAGE_SIZE = ROWS * COLS; // 54 per page
const SPIRAL_ORDER = generateSpiralOrder(ROWS, COLS);

// ── Spark particle ────────────────────────────────────────────────────────────
const Spark = ({ x, y, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 1000);
    return () => clearTimeout(t);
  }, [onDone]);

  const particles = Array.from({ length: 10 }, (_, i) => ({
    angle: (i / 10) * 360,
    dist: 24 + Math.random() * 32,
    size: 2 + Math.random() * 3,
    color: i % 3 === 0 ? "#ff8080" : i % 3 === 1 ? "#e81b23" : "#ffcc00",
  }));

  return (
    <div
      style={{
        position: "fixed",
        left: x,
        top: y,
        pointerEvents: "none",
        zIndex: 200,
      }}
    >
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            background: p.color,
            boxShadow: `0 0 8px ${p.color}`,
            left: 0,
            top: 0,
            animation: `spk${i} 1s ease-out forwards`,
          }}
        />
      ))}
      <style>
        {particles
          .map(
            (p, i) => `
        @keyframes spk${i} {
          0%   { opacity:1; transform:translate(-50%,-50%) scale(1.2); }
          100% { opacity:0; transform:translate(calc(-50% + ${Math.cos((p.angle * Math.PI) / 180) * p.dist}px),calc(-50% + ${Math.sin((p.angle * Math.PI) / 180) * p.dist}px)) scale(0); }
        }
      `,
          )
          .join("")}
      </style>
    </div>
  );
};

// ── Diamond Card ──────────────────────────────────────────────────────────────
const DiamondCard = React.memo(({ user, spiralIndex, isNew, onNewDone }) => {
  const isEmpty = !user || user === ".";
  const firstName = !isEmpty ? (user.Firstname ?? "").trim() : null;
  const lastName = !isEmpty ? (user.Lastname ?? "").trim() : null;
  const delay = spiralIndex * 35;

  return (
    <div
      className={`gc-card ${isEmpty ? "gc-empty" : "gc-filled"} ${isNew ? "gc-new" : ""}`}
      style={{ "--delay": `${delay}ms`, "--spiral": spiralIndex }}
    >
      {/* Rotated diamond */}
      <div
        className="gc-diamond"
        onAnimationEnd={isNew ? onNewDone : undefined}
      >
        <svg className="gc-sil" viewBox="0 0 80 90" fill="none">
          <ellipse cx="40" cy="28" rx="16" ry="18" fill="currentColor" />
          <path d="M10 82 Q10 54 40 54 Q70 54 70 82" fill="currentColor" />
        </svg>
        {!isEmpty && (
          <>
            <div className="gc-inner-glow" />
            <div className="gc-shimmer" />
          </>
        )}
        {isNew && <div className="gc-burst" />}
        {/* Name — absolutely placed below the diamond tip, never overlapping */}
      {(firstName || lastName) && (
        <div className="gc-label">
          {firstName && <span className="gc-line">{firstName}</span>}
          {lastName && <span className="gc-line gc-line-sub">{lastName}</span>}
        </div>
      )}
      </div>
    </div>
  );
});

// ── Main ──────────────────────────────────────────────────────────────────────
const Welcomegrid = () => {
  const [userData, setUserData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [newIndices, setNewIndices] = useState(new Set());
  const [sparks, setSparks] = useState([]);
  const [transitioning, setTransitioning] = useState(false);
  const [slideDir, setSlideDir] = useState(1);
  const [visible, setVisible] = useState(true);
  const prevDataRef = useRef([]);
  const sparkIdRef = useRef(0);
  const stageRef = useRef(null);

  // ── fetch all check-ins ──
  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_ADMIN}/qrCode/listLatestCheckin?skip=0&limit=10000`,
      );
      const data = await res.json();
      const prev = prevDataRef.current;
      const newIdxs = new Set();

      data.forEach((u, i) => {
        const changed =
          !prev[i] ||
          prev[i].Firstname !== u.Firstname ||
          prev[i].Lastname !== u.Lastname;
        if (changed) {
          newIdxs.add(i);
          const page = Math.floor(i / PAGE_SIZE);
          if (page === currentPage && stageRef.current) {
            const sp = SPIRAL_ORDER[i % PAGE_SIZE];
            if (sp) {
              const rect = stageRef.current.getBoundingClientRect();
              const cardPx =
                parseFloat(
                  getComputedStyle(document.documentElement).getPropertyValue(
                    "--card",
                  ),
                ) || 80;
              const colGap =
                parseFloat(
                  getComputedStyle(document.documentElement).getPropertyValue(
                    "--col-gap",
                  ),
                ) || 14;
              const rowStep = cardPx * 0.72 + 28;
              const colStep = cardPx + colGap;
              const rowOff = sp.c % 2 === 1 ? rowStep / 2 : 0;
              const id = ++sparkIdRef.current;
              setSparks((s) => [
                ...s,
                {
                  id,
                  x: rect.left + sp.c * colStep + cardPx / 2,
                  y: rect.top + sp.r * (cardPx * 0.55) + rowOff + cardPx / 2,
                },
              ]);
            }
          }
        }
      });

      if (newIdxs.size > 0) setNewIndices((p) => new Set([...p, ...newIdxs]));
      prevDataRef.current = data;
      setUserData(data);
    } catch (e) {
      console.error("Fetch error:", e);
    }
  }, [currentPage]);

  useEffect(() => {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    fetchData();
    const id = setInterval(fetchData, 5000);
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      clearInterval(id);
    };
  }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(userData.length / PAGE_SIZE));

  // ── navigate ──
  const goToPage = useCallback(
    (p) => {
      if (p === currentPage || transitioning || p < 0 || p >= totalPages)
        return;
      setSlideDir(p > currentPage ? 1 : -1);
      setTransitioning(true);
      setVisible(false);
      setTimeout(() => {
        setCurrentPage(p);
        setVisible(true);
        setTimeout(() => setTransitioning(false), 500);
      }, 340);
    },
    [currentPage, transitioning, totalPages],
  );

  // ── auto-advance every 10s ──
  useEffect(() => {
    if (totalPages <= 1) return;
    const id = setInterval(() => {
      setCurrentPage((prev) => {
        const next = (prev + 1) % totalPages;
        setSlideDir(1);
        setTransitioning(true);
        setVisible(false);
        setTimeout(() => {
          setVisible(true);
          setTimeout(() => setTransitioning(false), 500);
        }, 340);
        return next;
      });
    }, 10000);
    return () => clearInterval(id);
  }, [totalPages]);

  // ── build page grid ──
  const pageStart = currentPage * PAGE_SIZE;
  const pageGrid = Array(ROWS * COLS).fill(null);
  SPIRAL_ORDER.forEach((pos, i) => {
    pageGrid[pos.r * COLS + pos.c] = {
      user: userData[pageStart + i] ?? null,
      spiralIndex: i,
      globalIndex: pageStart + i,
    };
  });

  const removeNew = (gi) =>
    setNewIndices((s) => {
      const n = new Set(s);
      n.delete(gi);
      return n;
    });

  let stageClass = "wg-stage";
  if (!visible)
    stageClass += slideDir > 0 ? " wg-stage-out-l" : " wg-stage-out-r";
  else stageClass += " wg-stage-in";

  return (
    <>
      <style>{STYLES}</style>

      {sparks.map((sp) => (
        <Spark
          key={sp.id}
          x={sp.x}
          y={sp.y}
          onDone={() => setSparks((s) => s.filter((x) => x.id !== sp.id))}
        />
      ))}

      <div className="wg-root">
        {/* ── Optimized background — single static layer, no blur/animation ── */}
        <div className="wg-bg" />
        <div className="wg-vignette" />
        {/* One lightweight pulsing glow replacing 4 blurred orbs */}
        <div className="wg-glow-center" />
        {/* Corner decorations kept — they're cheap */}
        <div className="wg-corner tl" />
        <div className="wg-corner tr" />
        <div className="wg-corner bl" />
        <div className="wg-corner br" />

        {/* Header */}
        <header className="wg-header">
          <div className="wg-hd-left">
            <div className="wg-pulse-dot" />
            <span className="wg-live-tag">LIVE</span>
          </div>
          <div className="wg-hd-center">
            <span className="wg-h-line" />
            <h1 className="wg-title">CHECKED IN</h1>
            <span className="wg-h-line wg-h-line-r" />
          </div>
          <div className="wg-hd-right">
            <span className="wg-cnt-num" key={userData.length}>
              {userData.length}
            </span>
            <span className="wg-cnt-lbl">members</span>
          </div>
        </header>

        {/* Grid */}
        <div ref={stageRef} className={stageClass}>
          {Array.from({ length: ROWS }, (_, r) => (
            <div
              key={r}
              className={`wg-row ${r % 2 === 1 ? "wg-row-off" : ""}`}
            >
              {Array.from({ length: COLS }, (_, c) => {
                const slot = pageGrid[r * COLS + c];
                return (
                  <DiamondCard
                    key={c}
                    user={slot?.user ?? null}
                    spiralIndex={slot?.spiralIndex ?? 0}
                    isNew={slot ? newIndices.has(slot.globalIndex) : false}
                    onNewDone={
                      slot ? () => removeNew(slot.globalIndex) : undefined
                    }
                  />
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer / Pagination */}
        <footer className="wg-footer">
          {totalPages > 1 ? (
            <>
              <button
                className="wg-arrow"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 0}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18l-6-6 6-6"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <div className="wg-dots">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`wg-dot ${i === currentPage ? "wg-dot-on" : ""}`}
                    onClick={() => goToPage(i)}
                  />
                ))}
              </div>
              <button
                className="wg-arrow"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages - 1}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 18l6-6-6-6"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <div className="wg-pg-label">
                {currentPage + 1} / {totalPages}
              </div>
            </>
          ) : (
            <div style={{ height: 28 }} />
          )}
        </footer>
      </div>
    </>
  );
};

export default Welcomegrid;

// ── Styles ──────────────────────────────────────────────────────────────────
const STYLES = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --red:      #e81b23;
  --red-glow: rgba(232,27,35,0.55);
  --dark:     #080810;
  --card:     80px;
  --col-gap:  30px;
}

.wg-root {
  position: fixed; inset: 0;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  overflow: hidden;
  font-family: 'Helvetica Neue', Arial, sans-serif;
}

/* ─────────────────────────────────────────────────────────────────
   BACKGROUND — all expensive effects replaced with GPU-safe ones.

   REMOVED (were causing lag):
     • 4× .wg-orb  with filter:blur() + translate animations
       → blur forces a new compositing layer AND repaints every frame
     • 4× .wg-ring with scale() animations
       → each ring was doing a layout + paint on every tick
     • .wg-dotgrid animating background-position
       → background-position is not GPU-composited; triggers repaint
     • .wg-scanline animating top
       → top animation forces reflow every frame

   KEPT / REPLACED WITH:
     • Static radial-gradient background  (zero animation cost)
     • Single .wg-glow-center using opacity pulse only
       → opacity is composited on GPU, costs nothing
     • .wg-vignette static overlay        (zero cost)
     • Corner borders static              (zero cost)
     • .wg-pulse-dot opacity animation    (GPU composited, kept)
   ───────────────────────────────────────────────────────────────── */

/* Static base — paints once, never repaints */
.wg-bg {
  position: fixed; inset: 0; z-index: 0;
  will-change: auto;
  background:
    radial-gradient(ellipse 80% 60% at 15% 20%,  rgba(232,27,35,0.11) 0%, transparent 55%),
    radial-gradient(ellipse 70% 70% at 85% 80%,  rgba(140,6,12,0.09)  0%, transparent 55%),
    radial-gradient(ellipse 55% 55% at 50% 50%,  rgba(232,27,35,0.05) 0%, transparent 60%),
    linear-gradient(160deg, #06060e 0%, #0d0d1a 50%, #06060e 100%);
}

/* Single soft centre glow — only animates opacity (GPU layer, zero repaint) */
.wg-glow-center {
  position: fixed;
  top: 50%; left: 50%;
  width: 700px; height: 700px;
  margin: -350px 0 0 -350px;
  border-radius: 50%;
  z-index: 0;
  pointer-events: none;
  will-change: opacity;
  background: radial-gradient(circle, rgba(232,27,35,0.07) 0%, transparent 68%);
  animation: glowPulse 8s ease-in-out infinite;
}

/* Dark radial vignette — static, no animation */
.wg-vignette {
  position: fixed; inset: 0; z-index: 2; pointer-events: none;
  background: radial-gradient(ellipse 110% 110% at 50% 50%, transparent 38%, rgba(0,0,0,0.82) 100%);
}

/* ─ Corners — static, cheap ─ */
.wg-corner { position:fixed; width:44px; height:44px; pointer-events:none; z-index:5; opacity:0.55; }
.wg-corner.tl { top:16px;    left:16px;  border-top:2px solid var(--red); border-left:2px solid var(--red); }
.wg-corner.tr { top:16px;    right:16px; border-top:2px solid var(--red); border-right:2px solid var(--red); }
.wg-corner.bl { bottom:16px; left:16px;  border-bottom:2px solid var(--red); border-left:2px solid var(--red); }
.wg-corner.br { bottom:16px; right:16px; border-bottom:2px solid var(--red); border-right:2px solid var(--red); }

/* ─ Header ─ */
.wg-header { position:relative; z-index:10; display:flex; align-items:center; justify-content:space-between; width:100%; max-width:960px; padding:0 12px; margin-bottom:18px; animation:fadeDown 0.7s ease both; }
.wg-hd-left  { display:flex; align-items:center; gap:8px; min-width:70px; }
.wg-hd-center{ display:flex; align-items:center; gap:12px; flex:1; justify-content:center; }
.wg-hd-right { display:flex; align-items:baseline; gap:5px; min-width:70px; justify-content:flex-end; }
.wg-pulse-dot { width:8px; height:8px; border-radius:50%; background:var(--red); box-shadow:0 0 8px var(--red); will-change:opacity; animation:dotPulse 1.6s ease-in-out infinite; }
.wg-live-tag  { font-size:0.6rem; font-weight:800; letter-spacing:0.28em; color:var(--red); }
.wg-h-line    { display:block; height:1px; width:55px; background:linear-gradient(90deg,transparent,rgba(232,27,35,0.65)); }
.wg-h-line-r  { background:linear-gradient(90deg,rgba(232,27,35,0.65),transparent); }
.wg-title     { font-size:clamp(0.8rem,1.8vw,1.05rem); font-weight:900; letter-spacing:0.42em; color:#f0f0f8; text-transform:uppercase; text-shadow:0 0 22px rgba(232,27,35,0.28); }
.wg-cnt-num   { font-size:1.55rem; font-weight:900; color:var(--red); line-height:1; text-shadow:0 0 18px var(--red-glow); animation:countPop 0.35s cubic-bezier(0.22,1,0.36,1); }
.wg-cnt-lbl   { font-size:0.58rem; font-weight:700; letter-spacing:0.13em; color:rgba(180,180,200,0.38); text-transform:uppercase; }

/* ─ Stage ─ */
.wg-stage { position:relative; z-index:10; display:flex; flex-direction:column; align-items:center; will-change:transform,opacity; }
.wg-stage-in    { animation:stageIn   0.45s cubic-bezier(0.22,1,0.36,1) both; }
.wg-stage-out-l { animation:stageOutL 0.32s ease-in forwards; }
.wg-stage-out-r { animation:stageOutR 0.32s ease-in forwards; }

/* ─ Rows ─ */
.wg-row {
  display: flex;
  flex-direction: row;
  gap: var(--col-gap);
  margin-bottom: calc(var(--card) * -0.70);
  position: relative;
  z-index: 1;
}
.wg-row-off {
  transform: translateX(calc((var(--card) + var(--col-gap)) * 0.5));
  z-index: 2;
}

/* ─ Card wrapper ─ */
.gc-card {
  position: relative;
  width:  var(--card);
  height: calc(var(--card) * 1.22 + 32px);
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  animation: cardIn 0.55s cubic-bezier(0.22,1,0.36,1) var(--delay,0ms) forwards;
  flex-shrink: 0;
}
.gc-new {
  animation:
    cardIn   0.55s cubic-bezier(0.22,1,0.36,1) var(--delay,0ms) forwards,
    newFlash 0.65s ease                         var(--delay,0ms) forwards !important;
}

/* ─ Diamond (rotated square) ─ */
.gc-diamond {
  width:  var(--card);
  height: var(--card);
  transform: rotate(45deg);
  border-radius: 10px;
  position: relative;
  flex-shrink: 0;
  margin-top: calc(var(--card) * 0.11);
  transition: box-shadow 0.35s ease, transform 0.3s ease;
}
.gc-empty .gc-diamond {
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(232,27,35,0.1);
}
.gc-filled .gc-diamond {
  background: linear-gradient(135deg, rgba(232,27,35,0.26) 0%, rgba(125,7,13,0.4) 100%);
  border: 1px solid rgba(232,27,35,0.52);
  box-shadow: 0 0 16px rgba(232,27,35,0.2), inset 0 0 12px rgba(232,27,35,0.1);
  animation: tilePulse 4s ease-in-out calc(var(--spiral,0) * 80ms) infinite;
}
.gc-filled:hover .gc-diamond {
  transform: rotate(45deg) scale(1.1);
  box-shadow: 0 0 44px rgba(232,27,35,0.6), inset 0 0 22px rgba(232,27,35,0.22);
}

/* ─ Person silhouette ─ */
.gc-sil {
  position: absolute; width:62%; height:62%;
  top:50%; left:50%;
  transform: translate(-50%,-50%) rotate(-45deg);
}
.gc-empty  .gc-sil { color: rgba(255,255,255,0.045); }
.gc-filled .gc-sil { color: rgba(0,0,0,0.48); }

/* Inner glow & shimmer */
.gc-inner-glow {
  position: absolute; inset:0;
  background: radial-gradient(circle at 42% 36%, rgba(255,110,110,0.2), transparent 60%);
  animation: igPulse 3.5s ease-in-out calc(var(--spiral,0) * 55ms) infinite;
}
.gc-shimmer {
  position: absolute; inset:0;
  border-radius: 10px;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.13) 50%, transparent 100%);
  background-size: 200% 100%;
  background-position: -200% center;
  animation: shimmer 5s ease-in-out calc(var(--spiral,0) * 100ms) infinite;
}

/* Burst on new check-in */
.gc-burst {
  position: absolute; inset: -8px;
  border: 2px solid var(--red); border-radius:12px;
  animation: burstOut 0.8s ease-out forwards;
}

/* ─ Name label ─ */
.gc-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(var(--card) * 1);
  max-width: 148px;
  text-align: center;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  animation: lblFade 0.4s ease 0.25s both;
}
.gc-line {
  display: block;
  font-size: 0.53rem;
  transform: translateY(-50%) rotate(-45deg);
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #eeeff8;
  text-shadow: 0 0 12px rgba(232,27,35,0.95), 0 1px 6px rgba(0,0,0,0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.2;
  text-wrap: wrap;
}
.gc-line-sub {
  font-size: 0.46rem;
  font-weight: 700;
  color: rgba(220,221,240,0.78);
  text-shadow: 0 0 10px rgba(232,27,35,0.7), 0 1px 4px rgba(0,0,0,0.9);
  letter-spacing: 0.05em;
}

/* ─ Footer ─ */
.wg-footer {
  position: relative; z-index:10;
  margin-top: 36px;
  display: flex; align-items:center; gap:10px;
  animation: fadeUp 0.6s ease 0.4s both;
  min-height: 34px;
}
.wg-dots { display:flex; gap:7px; align-items:center; flex-wrap:wrap; max-width:520px; justify-content:center; }
.wg-dot { width:7px; height:7px; border-radius:50%; border:1px solid rgba(232,27,35,0.3); background:transparent; cursor:pointer; padding:0; transition:all 0.25s ease; }
.wg-dot-on { background:var(--red); border-color:var(--red); box-shadow:0 0 10px var(--red-glow); transform:scale(1.35); }
.wg-dot:hover:not(.wg-dot-on) { border-color:rgba(232,27,35,0.65); background:rgba(232,27,35,0.18); }
.wg-arrow { background:rgba(232,27,35,0.09); border:1px solid rgba(232,27,35,0.22); border-radius:7px; color:rgba(232,27,35,0.75); width:28px; height:28px; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all 0.2s ease; padding:0; }
.wg-arrow:hover:not(:disabled) { background:rgba(232,27,35,0.22); border-color:var(--red); color:var(--red); box-shadow:0 0 12px rgba(232,27,35,0.32); }
.wg-arrow:disabled { opacity:0.18; cursor:default; }
.wg-pg-label { position:absolute; right:-72px; font-size:0.58rem; font-weight:700; letter-spacing:0.18em; color:rgba(180,180,200,0.28); text-transform:uppercase; white-space:nowrap; }

/* ─ Keyframes ─ */

/* Background */
@keyframes glowPulse { 0%,100%{opacity:0.6;} 50%{opacity:1;} }

/* Grid */
@keyframes gridDrift  { 0%{background-position:0 0;} 100%{background-position:30px 30px;} }
@keyframes dotPulse   { 0%,100%{opacity:1;box-shadow:0 0 8px var(--red);}  50%{opacity:0.5;box-shadow:0 0 18px var(--red);} }
@keyframes cardIn     { 0%{opacity:0;transform:scale(0.4) rotate(160deg);}  65%{opacity:1;}  100%{opacity:1;transform:scale(1) rotate(0deg);} }
@keyframes newFlash   { 0%{filter:brightness(1);}  25%{filter:brightness(3) saturate(2);}  100%{filter:brightness(1);} }
@keyframes tilePulse  { 0%,100%{box-shadow:0 0 16px rgba(232,27,35,0.2),inset 0 0 12px rgba(232,27,35,0.1);}  50%{box-shadow:0 0 30px rgba(232,27,35,0.42),inset 0 0 20px rgba(232,27,35,0.18);} }
@keyframes igPulse    { 0%,100%{opacity:0.55;}  50%{opacity:1;} }
@keyframes shimmer    { 0%,65%{background-position:-200% center;}  80%{background-position:200% center;}  100%{background-position:200% center;} }
@keyframes burstOut   { 0%{opacity:1;transform:scale(0.7);}  100%{opacity:0;transform:scale(2.2);} }
@keyframes lblFade    { from{opacity:0;transform:translateX(-50%) translateY(6px);}  to{opacity:1;transform:translateX(-50%) translateY(0);} }

/* Stage slide */
@keyframes stageIn    { from{opacity:0;transform:translateX(60px);}  to{opacity:1;transform:translateX(0);} }
@keyframes stageOutL  { from{opacity:1;transform:translateX(0);}     to{opacity:0;transform:translateX(-70px);} }
@keyframes stageOutR  { from{opacity:1;transform:translateX(0);}     to{opacity:0;transform:translateX(70px);} }

/* Header/footer */
@keyframes fadeDown   { from{opacity:0;transform:translateY(-16px);}  to{opacity:1;transform:translateY(0);} }
@keyframes fadeUp     { from{opacity:0;transform:translateY(16px);}   to{opacity:1;transform:translateY(0);} }
@keyframes countPop   { 0%{transform:scale(1.5);}  100%{transform:scale(1);} }

/* ─ Responsive ─ */
@media (max-width: 860px) {
  :root { --card: 58px; --col-gap: 10px; }
  .gc-line     { font-size: 0.42rem; }
  .gc-line-sub { font-size: 0.38rem; }
  .wg-pg-label { display: none; }
  .wg-footer   { margin-top: 28px; }
}
@media (min-width: 1440px) {
  :root { --card: 96px; --col-gap: 18px; }
  .gc-line     { font-size: 0.62rem; }
  .gc-line-sub { font-size: 0.55rem; }
}
`;